import { getBookWithChapters } from '@/db/queries';
import { getBookCover, getPlaceholderImage, formatRelativeTime, slugify } from '@/lib/utils';
import type { BookChapter } from '@/db/schema';

<const/id=$global.params.id/>

<!-- Navigation -->
<div class="border-b">
  <div class="container mx-auto px-4">
    <a href="/" class="inline-flex items-center py-4 text-sm text-muted-foreground hover:text-primary">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2">
        <path d="m15 18-6-6 6-6"/>
      </svg>
      Back to Home
    </a>
  </div>
</div>

<try>
  <@placeholder>
    <section class="container mx-auto px-4 py-8">
      <div class="grid grid-cols-1 md:grid-cols-[256px_1fr] gap-8">
        <!-- Cover Image Skeleton -->
        <div class="relative aspect-[2/3] overflow-hidden rounded-lg bg-muted animate-pulse"></div>

        <!-- Info Skeleton -->
        <div class="space-y-6">
          <div class="space-y-4">
            <div class="h-12 bg-muted rounded w-3/4 animate-pulse"></div>
            <div class="h-6 bg-muted rounded w-1/2 animate-pulse"></div>
            <div class="h-20 bg-muted rounded animate-pulse"></div>
            <div class="flex gap-4">
              <div class="h-12 w-32 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </@placeholder>

  <@catch|error|>
    <section class="container mx-auto px-4 py-8">
      <div class="text-center py-12">
        <h2 class="text-xl font-semibold mb-2">Failed to load book</h2>
        <p class="text-muted-foreground mb-4">
          ${error instanceof Error ? error.message : 'An error occurred while loading the book.'}
        </p>
        <a href="/books">
          <button class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
            Browse Books
          </button>
        </a>
      </div>
    </section>
  </@catch>

  <await|data|=getBookWithChapters(id)>
    <if=!data.book>
      <section class="container mx-auto px-4 py-8">
        <div class="text-center py-12">
          <h2 class="text-xl font-semibold mb-2">Book not found</h2>
          <p class="text-muted-foreground mb-4">
            The book you're looking for doesn't exist or has been removed.
          </p>
          <a href="/books">
            <button class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
              Browse Books
            </button>
          </a>
        </div>
      </section>
    </if>
    <else>
      <const/book=data.book>
      <const/chapters=data.chapters>
      <const/coverPath=getBookCover(book)>
      <const/placeholderPath=getPlaceholderImage()>
      <const/onErrorScript=`this.src='${placeholderPath}'`>
      <const/bookYear=book.publishedAt ? new Date(book.publishedAt).getFullYear() : null/>
      <const/bookStatus=book.status || 'On-going'>
      <const/bookType=book.type || 'comic'>
      <const/firstChapterData=((chapters.length > 0) ? chapters[chapters.length - 1] : null) as BookChapter | null>

      <!-- Comic Header -->
      <section class="container mx-auto px-4 py-8">
        <div class="grid grid-cols-1 md:grid-cols-[256px_1fr] gap-8">
          <!-- Cover Image -->
          <div class="relative aspect-[2/3] overflow-hidden rounded-lg shadow-xl">
            <img
              src=coverPath
              alt=`${book.title} Cover`
              onerror=onErrorScript
              class="object-cover w-full h-full"
              width="300"
              height="450"
            />
          </div>

          <!-- Comic Info -->
          <div class="space-y-6">
            <div class="space-y-4">
              <div class="flex items-center gap-3">
                <span class="border border-input bg-background px-3 py-1 rounded text-xs font-medium uppercase">
                  ${bookStatus}
                </span>
              </div>

              <h1 class="text-4xl font-bold">${book.title}</h1>

              <div class="flex items-center gap-3">
                <span class="bg-secondary px-3 py-1 rounded text-xs font-medium capitalize">
                  ${bookType}
                </span>
              </div>

              <div class="flex items-center gap-4 text-muted-foreground">
                <if=book.author>
                  <span class="flex items-center gap-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    ${book.author}
                  </span>
                </if>
                <span class="flex items-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                  ${chapters.length} Issues
                </span>
                <if=bookYear>
                  <span class="flex items-center gap-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    ${bookYear}
                  </span>
                </if>
              </div>
            </div>

            <if=book.premise>
              <p class="text-lg text-muted-foreground">${book.premise}</p>
            </if>

            <div class="flex flex-wrap gap-4">
              <if=firstChapterData>
                <const/firstChapterSlug=slugify(firstChapterData.title || '')/>
                <const/firstChapterUrl=`/chapters/${firstChapterData.id}/${firstChapterSlug}`>
                <a href=firstChapterUrl>
                  <button class="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2">
                    Read Now
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                    </svg>
                  </button>
                </a>
              </if>
            </div>
          </div>
        </div>
      </section>

      <!-- Chapters Section -->
      <section class="container mx-auto px-4 py-8">
        <div class="rounded-lg border bg-card shadow-sm lg:p-4">
          <div class="p-6 border-b">
            <h2 class="text-2xl font-bold">Chapters</h2>
            <p class="text-sm text-muted-foreground mt-1">
              View the complete list of chapters for this book.
            </p>
          </div>
          <div class="p-6">
            <if=chapters.length === 0>
              <p class="text-center text-muted-foreground py-8">No chapters available yet.</p>
            </if>
            <else>
              <div class="flex flex-col space-y-4">
                <for|chapter| of=chapters>
                  <const/chapterSlug=slugify(chapter.title || '')/>
                  <const/chapterUrl=`/chapters/${chapter.id}/${chapterSlug}`>
                  <const/chapterNumber=chapter.number || ''>
                  <const/chapterTime=formatRelativeTime(chapter.publishedAt)>

                  <a href=chapterUrl>
                    <div class="group rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow">
                      <div class="flex items-center gap-4 p-4">
                        <div class="flex-1">
                          <h3 class="font-bold group-hover:text-primary transition-colors">
                            <if=chapterNumber>
                              Issue ${chapterNumber}:${' '}
                            </if>
                            ${chapter.title}
                          </h3>
                          <p class="text-sm text-muted-foreground">
                            ${chapterTime}
                          </p>
                        </div>
                        <button class="h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </a>
                </for>
              </div>
            </else>
          </div>
        </div>
      </section>
    </else>
  </await>
</try>
